import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useCountry } from "@/hooks/useCountry";
import { getCountryRoute } from "@/utils/countryRoutes";

type Country = "RO" | "UK" | "DE";

const COPY: Record<Country, {
  privateTitle: string;
  privateSubtitle: string;
  dealerTitle: string;
  dealerSubtitle: string;
  currency: string;
  perMonth: string;
  perListing: string;
  free: string;
  daysActive: (n: number) => string;
  untilSold: string;
  photosLabel: (n: number) => string;
  listingsLabel: (n: number | "unlimited") => string;
  featuredLabel: (n: number) => string;
  visibilityLabel: (n: number) => string;
  mostPopular: string;
  publishCta: string;
  private: {
    standard: { name: string; features: string[] };
    promoted: { name: string; features: string[] };
    untilSold: { name: string; features: string[] };
  };
  dealer: {
    start: { name: string; features: string[] };
    standard: { name: string; features: string[] };
    mare: { name: string; features: string[] };
  };
  taxNote: string;
}> = {
  RO: {
    privateTitle: "Persoane fizice",
    privateSubtitle: "Vinzi mașina ta? Alege pachetul potrivit — plată o singură dată, fără comision la vânzare.",
    dealerTitle: "Dealeri auto",
    dealerSubtitle: "Publică stocul complet și primești lead-uri direct. Fără comision pe vânzări.",
    currency: "€",
    perMonth: "/lună",
    perListing: "/anunț",
    free: "Gratuit",
    daysActive: (n) => `${n} zile activ`,
    untilSold: "Activ până la vânzare",
    photosLabel: (n) => `${n} fotografii per anunț`,
    listingsLabel: (n) => (n === "unlimited" ? "Anunțuri nelimitate" : `${n} anunțuri active`),
    featuredLabel: (n) => (n === 0 ? "Fără sloturi promovate" : `${n} slot${n > 1 ? "uri" : ""} promovat${n > 1 ? "e" : ""}`),
    visibilityLabel: (n) => `Vizibilitate: nivel ${n}/5`,
    mostPopular: "Cel mai popular",
    publishCta: "Publică un anunț",
    private: {
      standard: { name: "Standard", features: ["20 fotografii", "30 zile activ", "Prioritate ușoară în listă, timp de 30 de zile"] },
      promoted: { name: "Promovat", features: ["30 fotografii", "30 zile activ", "Afișare prioritară timp de 30 de zile", "Insignă Promovat"] },
      untilSold: { name: "Activ până la vânzare", features: ["30 fotografii", "Reînnoire automată la 30 zile", "Se oprește automat la vânzare", "Anulare oricând, fără rambursare"] },
    },
    dealer: {
      start: { name: "Start", features: ["Până la 5 anunțuri active", "Import CSV / XML", "Dashboard cu lead-uri", "0% comision la vânzare"] },
      standard: { name: "Standard", features: ["Până la 60 anunțuri active", "1 anunț promovat inclus", "Rapoarte de performanță"] },
      mare: { name: "Large", features: ["Anunțuri nelimitate", "3 anunțuri promovate incluse", "Prioritate în căutări", "Suport dedicat"] },
    },
    taxNote: "Prețurile sunt afișate fără TVA. Se adaugă TVA conform legislației din țara ta (21% România, 20% UK, 19% Germania, 20% Franța). Facturare automată prin Stripe.",
  },
  UK: {
    privateTitle: "Private sellers",
    privateSubtitle: "Selling your car? Pick the right package — one-off payment, no sales commission.",
    dealerTitle: "Car dealers",
    dealerSubtitle: "List your full stock and receive leads directly. No sales commission.",
    currency: "£",
    perMonth: "/month",
    perListing: "/listing",
    free: "Free",
    daysActive: (n) => `${n} days live`,
    untilSold: "Active until sold",
    photosLabel: (n) => `${n} photos per listing`,
    listingsLabel: (n) => (n === "unlimited" ? "Unlimited listings" : `${n} active listings`),
    featuredLabel: (n) => (n === 0 ? "No featured slots" : `${n} featured slot${n > 1 ? "s" : ""}`),
    visibilityLabel: (n) => `Visibility: level ${n}/5`,
    mostPopular: "Most popular",
    publishCta: "Publish a listing",
    private: {
      standard: { name: "Standard", features: ["20 photos", "30 days live", "Boosted visibility"] },
      promoted: { name: "Promoted", features: ["30 photos", "30 days live", "Top visibility", "Promoted badge"] },
      untilSold: { name: "Active until sold", features: ["30 photos", "Auto-renews every 30 days", "Auto-stops on sale", "Cancel anytime, no refund"] },
    },
    dealer: {
      start: { name: "Start", features: ["Up to 5 active listings", "CSV / XML import", "Leads dashboard", "0% sales commission"] },
      standard: { name: "Standard", features: ["Up to 60 active listings", "1 featured slot included", "Performance reports"] },
      mare: { name: "Large", features: ["Unlimited listings", "3 featured slots included", "Search priority", "Dedicated support"] },
    },
    taxNote: "Prices are shown excluding VAT. VAT is added per your country's legislation (21% Romania). Invoices issued automatically via Stripe.",
  },
  DE: {
    privateTitle: "Privatverkäufer",
    privateSubtitle: "Auto verkaufen? Wählen Sie das passende Paket — einmalige Zahlung, keine Verkaufsprovision.",
    dealerTitle: "Autohändler",
    dealerSubtitle: "Veröffentlichen Sie Ihren Bestand und erhalten Sie Leads direkt. Keine Verkaufsprovision.",
    currency: "€",
    perMonth: "/Monat",
    perListing: "/Inserat",
    free: "Kostenlos",
    daysActive: (n) => `${n} Tage aktiv`,
    untilSold: "Aktiv bis verkauft",
    photosLabel: (n) => `${n} Fotos pro Inserat`,
    listingsLabel: (n) => (n === "unlimited" ? "Unbegrenzte Inserate" : `${n} aktive Inserate`),
    featuredLabel: (n) => (n === 0 ? "Keine Premium-Slots" : `${n} Premium-Slot${n > 1 ? "s" : ""}`),
    visibilityLabel: (n) => `Sichtbarkeit: Stufe ${n}/5`,
    mostPopular: "Beliebteste Wahl",
    publishCta: "Inserat aufgeben",
    private: {
      standard: { name: "Standard", features: ["20 Fotos", "30 Tage aktiv", "Erhöhte Sichtbarkeit"] },
      promoted: { name: "Premium", features: ["30 Fotos", "30 Tage aktiv", "Maximale Sichtbarkeit", "Premium-Badge"] },
      untilSold: { name: "Aktiv bis verkauft", features: ["30 Fotos", "Automatische Verlängerung alle 30 Tage", "Stoppt automatisch bei Verkauf", "Jederzeit kündbar, keine Rückerstattung"] },
    },
    dealer: {
      start: { name: "Start", features: ["Bis zu 5 aktive Inserate", "CSV / XML Import", "Leads-Dashboard", "0% Verkaufsprovision"] },
      standard: { name: "Standard", features: ["Bis zu 60 aktive Inserate", "1 Premium-Slot inklusive", "Leistungsberichte"] },
      mare: { name: "Groß", features: ["Unbegrenzte Inserate", "3 Premium-Slots inklusive", "Suchpriorität", "Dedizierter Support"] },
    },
    taxNote: "Preise verstehen sich zzgl. MwSt. Die MwSt. richtet sich nach dem Recht Ihres Landes (21% Rumänien). Rechnungen automatisch über Stripe.",
  },
};

interface TierCardProps {
  name: string;
  price: string;
  priceSuffix: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

const TierCard = ({ name, price, priceSuffix, features, highlighted, badge, ctaLabel, ctaHref }: TierCardProps) => (
  <Card className={`relative p-6 flex flex-col ${highlighted ? "border-2 border-primary shadow-lg" : "border"}`}>
    {badge && (
      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
        {badge}
      </Badge>
    )}
    <div className="text-lg font-semibold mb-2">{name}</div>
    <div className="mb-4">
      <span className="text-3xl font-bold">{price}</span>
      {priceSuffix && <span className="text-sm text-muted-foreground ml-1">{priceSuffix}</span>}
    </div>
    <ul className="space-y-2 text-sm text-muted-foreground flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2">
          <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    {ctaHref && ctaLabel && (
      <Button asChild className="mt-4 w-full">
        <Link to={ctaHref}>{ctaLabel}</Link>
      </Button>
    )}
  </Card>
);

export function PlansOverview() {
  const { country } = useCountry();
  const c = COPY[country] ?? COPY.RO;
  const publishHref = getCountryRoute(country, "/vinde-masina");

  return (
    <div className="space-y-12 mb-12">
      {/* Persoane fizice */}
      <div>
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">{c.privateTitle}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">{c.privateSubtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <TierCard
            name={c.private.standard.name}
            price={country === 'UK' ? '£9.99' : '9,99 €'}
            priceSuffix={c.perListing}
            features={c.private.standard.features}
            ctaLabel={c.publishCta}
            ctaHref={publishHref}
          />
          <TierCard
            name={c.private.promoted.name}
            price={country === 'UK' ? '£14.99' : '14,99 €'}
            priceSuffix={c.perListing}
            features={c.private.promoted.features}
            highlighted
            badge={c.mostPopular}
            ctaLabel={c.publishCta}
            ctaHref={publishHref}
          />
          <TierCard
            name={c.private.untilSold.name}
            price={country === 'UK' ? '£19.99' : '19,99 €'}
            priceSuffix={c.perMonth}
            features={c.private.untilSold.features}
            ctaLabel={c.publishCta}
            ctaHref={publishHref}
          />
        </div>
      </div>

      {/* Dealeri */}
      <div>
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">{c.dealerTitle}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">{c.dealerSubtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TierCard
            name={c.dealer.start.name}
            price={country === 'UK' ? '£29.99' : '29,99 €'}
            priceSuffix={c.perMonth}
            features={c.dealer.start.features}
            ctaLabel="Creează cont dealer"
            ctaHref="/dealeri?plan=start"
          />
          <TierCard
            name={c.dealer.standard.name}
            price={country === 'UK' ? '£50' : '50 €'}
            priceSuffix={c.perMonth}
            features={c.dealer.standard.features}
            highlighted
            badge={c.mostPopular}
            ctaLabel="Creează cont dealer"
            ctaHref="/dealeri?plan=standard"
          />
          <TierCard
            name={c.dealer.mare.name}
            price={country === 'UK' ? '£120' : '120 €'}
            priceSuffix={c.perMonth}
            features={c.dealer.mare.features}
            ctaLabel="Creează cont dealer"
            ctaHref="/dealeri?plan=large"
          />
        </div>
        <p className="text-xs text-muted-foreground text-center mt-4">{country === 'RO' ? 'Insigna Dealer Verificat se acordă după verificare, indiferent de pachet.' : country === 'DE' ? 'Das Abzeichen „Geprüfter Händler“ wird nach der Prüfung unabhängig vom Paket vergeben.' : 'The Verified Dealer badge is awarded after verification, regardless of plan.'}</p>
        <p className="text-xs text-muted-foreground text-center mt-2">{c.taxNote}</p>
      </div>
    </div>
  );
}
